import { useState } from 'react';
import { supabase } from '../supabase.js';
import { Field } from './FormBits.jsx';

// Session 13: approval modes. pack.approval = {mode, deadline_days,
// reminder_days_before}; defaults client / 5 / 2. Validated again server side
// by portal_create_client / portal_update_client.

export const APPROVAL_MODES = [
  ['client', 'Client approves', 'Nothing moves until the client approves the plan and each draft.'],
  ['client_deadline', 'Client approves, or it\'s automatic after N days',
    'Silence means yes. One reminder goes out first. Agency review stays on so you see everything before the client does.'],
  ['agency_only', 'Only I approve (no client sign-off)',
    'You are the final approver. The client gets no portal invite and no pipeline emails; every notice comes to you.'],
];

export const APPROVAL_DEFAULTS = { mode: 'client', deadline_days: 5, reminder_days_before: 2 };

export function approvalFromPack(pack) {
  const a = pack?.approval || {};
  return {
    mode: a.mode || 'client',
    deadline_days: a.deadline_days ?? 5,
    reminder_days_before: a.reminder_days_before ?? 2,
  };
}

export function approvalPayload(a) {
  return { mode: a.mode, deadline_days: Number(a.deadline_days), reminder_days_before: Number(a.reminder_days_before) };
}

// The Approval section used by Add new client and Settings.
export function ApprovalFields({ value, onChange, disabled, lockedNote }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="field approval-fields">
      <span className="field-label">Approval</span>
      {lockedNote && <span className="field-hint">{lockedNote}</span>}
      {APPROVAL_MODES.map(([k, label, hint]) => (
        <label key={k} className={`check approval-option ${disabled ? 'disabled' : ''}`}>
          <input type="radio" name="approval-mode" value={k} checked={value.mode === k} disabled={disabled}
                 onChange={() => set('mode', k)} />
          <span>
            <span className="approval-option-label">{k === 'client_deadline'
              ? `Client approves, or it's automatic after ${value.deadline_days || 'N'} days` : label}</span>
            <span className="field-hint">{hint}</span>
          </span>
        </label>
      ))}
      {value.mode === 'client_deadline' && (
        <div className="inline-fields" style={{ marginTop: 8 }}>
          <Field label="Days to respond" hint="Between 3 and 14. The deadline is 11:59pm in the client's timezone.">
            <input type="number" min={3} max={14} style={{ maxWidth: 90 }} value={value.deadline_days}
                   disabled={disabled} onChange={(e) => set('deadline_days', e.target.value)} />
          </Field>
          <Field label="Reminder, days before" hint={`One reminder email, between 1 and ${Math.max(1, Number(value.deadline_days || 5) - 1)} days before the deadline.`}>
            <input type="number" min={1} max={Math.max(1, Number(value.deadline_days || 5) - 1)} style={{ maxWidth: 90 }}
                   value={value.reminder_days_before} disabled={disabled}
                   onChange={(e) => set('reminder_days_before', e.target.value)} />
          </Field>
        </div>
      )}
    </div>
  );
}

// "Tuesday, Oct 13". A deadline is 23:59 in the client's timezone; reading it
// 12 hours earlier keeps the calendar day right for any viewer within 11
// hours of that timezone.
export function fmtDay(iso, { deadline = false } = {}) {
  if (!iso) return '';
  let d = String(iso).length <= 10 ? new Date(String(iso) + 'T12:00:00') : new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  if (deadline) d = new Date(d.getTime() - 12 * 3600 * 1000);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

export function DeadlineNote({ at, agency, className }) {
  if (!at) return null;
  return (
    <p className={`deadline-note ${className || ''}`}>
      {agency
        ? `Approves automatically on ${fmtDay(at, { deadline: true })} if the client doesn't respond`
        : `Approves automatically on ${fmtDay(at, { deadline: true })} if we don't hear from you`}
    </p>
  );
}

export function AutoApprovedNote({ at }) {
  if (!at) return null;
  return <p className="deadline-note deadline-done">Approved automatically on {fmtDay(at)}</p>;
}

export function AutoBadge() {
  return <span className="auto-badge" title="Approved automatically after the deadline">auto</span>;
}

// "How this works" on the client's Plan page: shown until the user dismisses
// it; the dismissal is stored server side (portal_dismiss_explainer).
export const PLAN_EXPLAINER_KEY = 'plan_how_it_works';

export function HowItWorks({ mode, days, dismissed, onDismissed }) {
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);
  if (dismissed || hidden || mode === 'agency_only') return null;
  const steps = [
    'You\'ll receive your next 3 months of blog titles. Approve each one, or mark it Replace.',
    'There\'s a comments box at the bottom of the plan. We\'ll go back and forth until the plan is complete.',
    'Once the plan is set, your first set of questions arrives about a week before your first post.',
    'Then you\'ll receive each blog to approve before it goes live.',
  ];
  if (mode === 'client_deadline') {
    steps.push(`If you don't respond within ${days || 5} days, we'll treat it as approved and go ahead. We'll send you a reminder first.`);
  }
  async function dismiss() {
    setBusy(true);
    const { error } = await supabase.rpc('portal_dismiss_explainer', { p_key: PLAN_EXPLAINER_KEY });
    setBusy(false);
    if (!error) { setHidden(true); onDismissed?.(); }
  }
  return (
    <section className="explainer" aria-label="How this works">
      <div className="explainer-head">
        <h2>How this works</h2>
        <button type="button" className="link-btn" onClick={dismiss} disabled={busy}>{busy ? 'Closing…' : 'Got it, hide this'}</button>
      </div>
      <ol>
        {steps.map((s, i) => <li key={i}>{s}</li>)}
      </ol>
    </section>
  );
}
